/* eslint-disable @typescript-eslint/no-explicit-any */
// Hub widget registry entries (simple definitions for the widget picker / registry).
// These are distinct from the rich HUB_WIDGETS in hubWidgets.ts which include getData functions.

import {
  REGISTRY_WIDGET_CATEGORIES,
  REGISTRY_WIDGET_SIZES,
  REGISTRY_WIDGET_TYPES,
  REGISTRY_ICONS,
} from './widgetRegistryConstants';

const C = REGISTRY_WIDGET_CATEGORIES;
const S = REGISTRY_WIDGET_SIZES;
const T = REGISTRY_WIDGET_TYPES;

export const HUB_REGISTRY_WIDGETS = [
  // HUB WIDGETS
  {
    id: 'active-projects',
    name: 'Active Projects',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Number of active projects',
    icon: REGISTRY_ICONS.FiBarChart2,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true,
    emptyStateConfig: 'hub'
  },
  {
    id: 'total-budget',
    name: 'Total Budget',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Aggregated budget across projects',
    icon: REGISTRY_ICONS.FiDollarSign,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'total-spent',
    name: 'Total Spent',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Aggregated spend across projects',
    icon: REGISTRY_ICONS.FiDollarSign,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'burn-rate',
    name: 'Burn Rate',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Rate of budget consumption',
    icon: REGISTRY_ICONS.FiTrendingUp,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'monthly-spend-trend',
    name: 'Monthly Spend Trend',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Bar chart of spending over time',
    icon: REGISTRY_ICONS.FiTrendingUp,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: true
  },
  {
    id: 'project-status-distribution',
    name: 'Project Status',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Projects by status',
    icon: REGISTRY_ICONS.FiBarChart2,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'recent-projects',
    name: 'Recent Projects',
    category: C.HUB,
    type: T.LIST,
    defaultSize: S.MEDIUM,
    description: 'Recently updated projects',
    icon: REGISTRY_ICONS.FiClipboard,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'unread-notifications',
    name: 'Unread Notifications',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Count of unread notifications',
    icon: REGISTRY_ICONS.FiBell,
    dataSource: 'hub',
    refreshRate: 30000,
    supportsDateFilter: false
  },
  {
    id: 'recent-notifications',
    name: 'Recent Notifications',
    category: C.HUB,
    type: T.LIST,
    defaultSize: S.MEDIUM,
    description: 'Latest system notifications',
    icon: REGISTRY_ICONS.FiBell,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: false
  },
  {
    id: 'hours-by-person',
    name: 'Hours by Person',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Time tracking per team member',
    icon: REGISTRY_ICONS.FiUsers,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: true
  },
  // HUB - Executive Command Center Widgets
  {
    id: 'system-health-score',
    name: 'System Health',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Aggregate health score across all platform modules',
    icon: REGISTRY_ICONS.FiHeart,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: false
  },
  {
    id: 'unified-total-cost',
    name: 'Total Platform Cost',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Combined LLM, Cloud, and Project costs',
    icon: REGISTRY_ICONS.FiDollarSign,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: true
  },
  {
    id: 'total-active-operations',
    name: 'Active Operations',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Active agents, pipelines, and deployments',
    icon: REGISTRY_ICONS.FiActivity,
    dataSource: 'hub',
    refreshRate: 30000,
    supportsDateFilter: false
  },
  {
    id: 'platform-error-rate',
    name: 'Platform Error Rate',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Aggregated error rate across all modules',
    icon: REGISTRY_ICONS.FiAlertCircle,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'module-status-grid',
    name: 'Module Status',
    category: C.HUB,
    type: T.CUSTOM,
    defaultSize: S.WIDE,
    description: 'Health status of all platform modules',
    icon: REGISTRY_ICONS.FiMonitor,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: false
  },
  {
    id: 'cross-platform-alerts',
    name: 'Platform Alerts',
    category: C.HUB,
    type: T.LIST,
    defaultSize: S.MEDIUM,
    description: 'Critical alerts from all platform modules',
    icon: REGISTRY_ICONS.FiAlertTriangle,
    dataSource: 'hub',
    refreshRate: 30000,
    supportsDateFilter: false
  },
  {
    id: 'llm-cost-trend',
    name: 'LLM Cost Trend',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'LLM spending over time',
    icon: REGISTRY_ICONS.FiTrendingUp,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: true
  },
  {
    id: 'agent-activity-summary',
    name: 'Agent Runs',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Total agent runs in period',
    icon: REGISTRY_ICONS.FiCpu,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'pipeline-health',
    name: 'Pipeline Health',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Data pipeline success rate',
    icon: REGISTRY_ICONS.FiDatabase,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'cloud-compliance-summary',
    name: 'Cloud Compliance',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Infrastructure compliance score',
    icon: REGISTRY_ICONS.FiShield,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: false
  },

  // AWS INSIGHTS WIDGETS (for AWS/Partner users)
  {
    id: 'project-health-heatmap',
    name: 'Project Health Map',
    category: C.HUB,
    type: T.CUSTOM,
    defaultSize: S.FULL,
    description: 'Heatmap showing project health across all clients',
    icon: REGISTRY_ICONS.FiGrid,
    dataSource: 'aws_insights',
    respectsClientFilter: false,
    refreshRate: 300000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'total-infrastructure-spend-combined',
    name: 'Total Infrastructure Spend',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.MEDIUM,
    description: 'Combined spent + estimated infrastructure costs',
    icon: REGISTRY_ICONS.FiDollarSign,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'infrastructure-budget-utilization',
    name: 'Budget Utilization',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.MEDIUM,
    description: 'Infrastructure budget utilization percentage',
    icon: REGISTRY_ICONS.FiTrendingUp,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'funding-active-requests',
    name: 'Active Funding Requests',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Number of active funding requests',
    icon: REGISTRY_ICONS.FiClipboard,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'funding-total-credits',
    name: 'Applied Credits',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Total applied AWS credits (all active credit codes)',
    icon: REGISTRY_ICONS.FiAward,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'funding-credits-expiring',
    name: 'Credits Expiring Soon',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Credits expiring within 30 days',
    icon: REGISTRY_ICONS.FiAlertTriangle,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'funding-approval-rate',
    name: 'Approval Rate',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Funding request approval rate',
    icon: REGISTRY_ICONS.FiCheckCircle,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'funding-by-program',
    name: 'Funding by Program',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Distribution of funding across AWS programs',
    icon: REGISTRY_ICONS.FiPieChart,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'funding-requests-timeline',
    name: 'Funding Timeline',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Funding requests over time',
    icon: REGISTRY_ICONS.FiClock,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'credits-by-client',
    name: 'Credits by Client',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Top clients by credit allocation',
    icon: REGISTRY_ICONS.FiBarChart2,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'credit-expiration-timeline',
    name: 'Credit Expiration',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Credits expiring over next 12 months',
    icon: REGISTRY_ICONS.FiClock,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'funding-recent-requests',
    name: 'Recent Funding Requests',
    category: C.HUB,
    type: T.LIST,
    defaultSize: S.MEDIUM,
    description: 'Latest funding request submissions',
    icon: REGISTRY_ICONS.FiClipboard,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },
  {
    id: 'projects-needing-attention',
    name: 'Projects At Risk',
    category: C.HUB,
    type: T.LIST,
    defaultSize: S.MEDIUM,
    description: 'Projects with yellow or red health status',
    icon: REGISTRY_ICONS.FiAlertTriangle,
    dataSource: 'aws_insights',
    respectsClientFilter: true,
    refreshRate: 60000,
    emptyStateConfig: 'hub'
  },

  // HUB WIDGETS (Additional)
  {
    id: 'pending-support',
    name: 'Pending Support Requests',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Count of open support tickets',
    icon: REGISTRY_ICONS.FiLifeBuoy,
    dataSource: 'hub',
    refreshRate: 30000,
    supportsDateFilter: false
  },
  {
    id: 'critical-alerts',
    name: 'Critical Alerts',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'High-priority alerts requiring attention',
    icon: REGISTRY_ICONS.FiAlertOctagon,
    dataSource: 'hub',
    refreshRate: 30000,
    supportsDateFilter: false
  },
  {
    id: 'ai-model-costs',
    name: 'AI Model Costs',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Cost distribution by AI model',
    icon: REGISTRY_ICONS.FiCpu,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: true
  },
  {
    id: 'project-progress',
    name: 'Project Progress',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Overall project completion',
    icon: REGISTRY_ICONS.FiTrendingUp,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: false
  },
  {
    id: 'remaining-budget',
    name: 'Remaining Budget',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Available budget amount',
    icon: REGISTRY_ICONS.FiDollarSign,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'total-hours-logged',
    name: 'Total Hours Logged',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Aggregated timesheet hours',
    icon: REGISTRY_ICONS.FiClock,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'billable-hours',
    name: 'Billable Hours',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Total billable time',
    icon: REGISTRY_ICONS.FiBriefcase,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'team-utilization',
    name: 'Team Utilization',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Resource utilization percentage',
    icon: REGISTRY_ICONS.FiUsers,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'hours-by-project',
    name: 'Hours by Project',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Time distribution across projects',
    icon: REGISTRY_ICONS.FiBarChart2,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: true
  },
  {
    id: 'support-response-time',
    name: 'Avg Response Time',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Average support response time',
    icon: REGISTRY_ICONS.FiZap,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: true
  },
  {
    id: 'support-by-priority',
    name: 'Support by Priority',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Tickets by priority level',
    icon: REGISTRY_ICONS.FiTarget,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'project-health-score',
    name: 'Project Health',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.MEDIUM,
    description: 'Overall project health indicator',
    icon: REGISTRY_ICONS.FiHeart,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: false
  },
  {
    id: 'milestone-progress',
    name: 'Milestone Progress',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.LARGE,
    description: 'Milestone completion status',
    icon: REGISTRY_ICONS.FiTarget,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: false
  },
  {
    id: 'assessment-score',
    name: 'Strategy Score',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.MEDIUM,
    description: 'AI strategy assessment score',
    icon: REGISTRY_ICONS.FiTarget,
    dataSource: 'hub',
    refreshRate: 600000,
    supportsDateFilter: false
  },
  {
    id: 'category-scores',
    name: 'Category Scores',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Scores by category',
    icon: REGISTRY_ICONS.FiBarChart2,
    dataSource: 'hub',
    refreshRate: 600000,
    supportsDateFilter: false
  },
  {
    id: 'projects-by-client',
    name: 'Projects by Client',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Project count per client',
    icon: REGISTRY_ICONS.FiUsers,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: false
  },
  {
    id: 'model-cost-distribution',
    name: 'Model Cost Distribution',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Cost distribution by model',
    icon: REGISTRY_ICONS.FiCpu,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: true
  },
  {
    id: 'non-billable-hours',
    name: 'Non-Billable Hours',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Total non-billable time',
    icon: REGISTRY_ICONS.FiClock,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: true
  },
  {
    id: 'recent-support-activity',
    name: 'Support Activity',
    category: C.HUB,
    type: T.LIST,
    defaultSize: S.MEDIUM,
    description: 'Latest support updates',
    icon: REGISTRY_ICONS.FiMessageSquare,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'activity-by-type',
    name: 'Activity by Type',
    category: C.HUB,
    type: T.CHART,
    defaultSize: S.MEDIUM,
    description: 'Actions distribution',
    icon: REGISTRY_ICONS.FiBarChart2,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: true
  },
  {
    id: 'user-activity-timeline',
    name: 'Activity Timeline',
    category: C.HUB,
    type: T.LIST,
    defaultSize: S.LARGE,
    description: 'Chronological user actions',
    icon: REGISTRY_ICONS.FiClock,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
  {
    id: 'assessment-completion-rate',
    name: 'Assessment Completion',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Completed assessments %',
    icon: REGISTRY_ICONS.FiClipboard,
    dataSource: 'hub',
    refreshRate: 600000,
    supportsDateFilter: false
  },
  {
    id: 'recommendations-count',
    name: 'Active Recommendations',
    category: C.HUB,
    type: T.METRIC,
    defaultSize: S.SMALL,
    description: 'Generated recommendations',
    icon: REGISTRY_ICONS.FiZap,
    dataSource: 'hub',
    refreshRate: 300000,
    supportsDateFilter: false
  },
  {
    id: 'client-communication-history',
    name: 'Communication History',
    category: C.HUB,
    type: T.LIST,
    defaultSize: S.LARGE,
    description: 'Message timeline',
    icon: REGISTRY_ICONS.FiMail,
    dataSource: 'hub',
    refreshRate: 120000,
    supportsDateFilter: true
  },
  {
    id: 'recent-audit-log',
    name: 'Recent Audit Log',
    category: C.HUB,
    type: T.LIST,
    defaultSize: S.MEDIUM,
    description: 'Recent system activity',
    icon: REGISTRY_ICONS.FiFileText,
    dataSource: 'hub',
    refreshRate: 60000,
    supportsDateFilter: true
  },
];
